/*************************************************************************
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { DOCUMENT_ASSEMBLY_PERM } from "./../ajsProvider/constants";

export const cloneDeep = obj => JSON.parse(JSON.stringify(obj));

export const delay = async interval =>
    new Promise(resolve => setTimeout(resolve, interval));

export const getVariablesFromQueryParams = queryParams => {
    const keyMap = {
        useDCAPI: { transform: val => val !== "false" },
        enableAJSLogging: { transform: val => val === "true" },
        useAJSFullBuild: { transform: val => val === "true" },
        useProgressiveRendering: { transform: val => val === "true" },
        ajsExceptionSupport: { transform: val => val },
        ajsSplitWasm: { transform: val => val === "true" },
    };

    const resp = {};
    Object.keys(keyMap).forEach(key => {
        if (key in queryParams) {
            resp[key] = keyMap[key].transform(queryParams[key]);
        }
    });
    return resp;
};

// eslint-disable-next-line no-bitwise
const isAllowed = (permissions, perm) => (permissions >>> perm) & 1;

export const canDoOrganize = async (metadata = {}) => {
    const response = {
        isSupported: true,
        reason: {},
    };

    const {
        isXFA,
        isPortfolio,
        isEncrypted,
        permissions,
        hasDigitalSignatures,
        hasAcroFormDict,
        hasSignMetadata,
        hasAcroFormFields,
    } = metadata;

    if (isXFA || isPortfolio || isEncrypted || hasDigitalSignatures || hasAcroFormDict) {
        const documentAssemblyAllowed = isAllowed(permissions, DOCUMENT_ASSEMBLY_PERM) === 1;
        response.isSupported = false;
        if (isXFA) response.reason.isXFA = true;
        if (isPortfolio) response.reason.isPortfolio = true;
        if (isEncrypted) {
            response.reason.isEncrypted = true;
            response.reason.isDocumentAssemblyAllowed = documentAssemblyAllowed;
        }
        if (hasDigitalSignatures) {
            response.reason.hasDigitalSignatures = true;
            response.reason.isDocumentAssemblyAllowed = documentAssemblyAllowed;
        }
        if (hasAcroFormDict) {
            response.reason.hasAcroFormDict = true;
        }
        if (hasSignMetadata) {
            response.reason.hasSignMetadata = true;
        }
        if (hasAcroFormFields) {
            response.reason.hasAcroFormFields = true;
        }
    }

    return response;
};

export const preloadAsset = (url, as = "script") => {
    if (document.head.querySelector(`link[href="${url}"][rel="preload"]`)) {
        // already preloaded
        return;
    }
    const link = document.createElement("link");
    link.href = url;
    link.as = as;
    link.rel = "preload";
    document.head.appendChild(link);
};

// eslint-disable-next-line camelcase
export const shouldUseWasmParts = () => !!window?.adobe_dc_data?.data?.file?.shouldUseWasmParts;
